/**
 * masterGanttDeepLinks.ts
 * Phase 4, Item 10 — Pure helper that resolves a Highcharts Gantt point to a
 * navigation target for the Master Gantt "deep link" feature.
 *
 * Resolves navigation for each taskType:
 *   machineBooking      → Machine Schedule pre-focused on the booking
 *   employeeAssignment  → Employee Schedule with the user row expanded to the
 *                         week containing the assignment
 *   workOrder           → Work Orders tab (/workorders)
 *   project             → Projects list (/projects)
 *   machinesGroup       → null  (group header — not navigable)
 *   staffGroup          → null  (group header — not navigable)
 *   machineSummary      → null  (BUG-2026-0194 per-machine / per-test-type row)
 *   personSummary       → null  (BUG-2026-0194 per-person row)
 *   unknown             → null
 *
 * BUG-2026-0194: an employeeAssignment bar may be a MERGE of several weekly
 * rows (custom.assignmentKeys). findBarContext gathers every folded row so the
 * drawer shows the whole span; the deep-link still focuses the first week.
 *
 * IMPORTANT:
 *   - This file is deliberately side-effect-free.
 *     It takes a plain object and returns a route descriptor.
 *   - The caller (MasterGanttPage.tsx) calls navigate() using the result.
 *   - `weekStart` for an employeeAssignment is derived from
 *     `point.custom.weekStart` which the row builder copies from
 *     MasterGanttEmployeeAssignment.weekStart (always a Saturday ISO string).
 *   - Conflict glyphs take precedence: the caller must check
 *     `point.custom.conflictType` BEFORE calling this function and handle
 *     conflict navigation separately (as item 4 already does).
 *
 * @returns RouteDescriptor with path + search params, or null if the point
 *          has no navigable target.
 */

import { ROUTES } from '../../../constants/routes.js';
import type {
  MasterGanttResponse,
  MasterGanttMachineBooking,
  MasterGanttEmployeeAssignment,
} from '../../../types/schedule.js';
import type { GanttBarDetail } from '../../../components/schedule/GanttDetailDrawer.js';
import { toMs, addDays } from '../../../components/schedule/masterGanttRowBuilder.js';
import { prepStepBreakdown } from '../../../utils/prepBookingMerge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The discriminator values emitted by masterGanttRowBuilder. */
export type GanttPointTaskType =
  | 'project'
  | 'workOrder'
  | 'machinesGroup'
  | 'staffGroup'
  | 'machineSummary'
  | 'personSummary'
  | 'machineBooking'
  | 'employeeAssignment';

/**
 * Minimal shape of the `custom` bag on a Highcharts GanttPoint as populated
 * by masterGanttRowBuilder + ganttConflictTagger.
 *
 * All fields are optional because the custom bag may be absent or partial
 * (e.g. group rows have no bookingKey / assignmentKey).
 */
export interface GanttPointCustom {
  bookingKey?: string;
  /** BUG-2026-0193 — every prep bookingKey folded into a merged "Preparation" bar. */
  bookingKeys?: string[];
  /** BUG-2026-0193 — true on a folded prep bar (drawer shows the combined summary). */
  isPrepMerge?: boolean;
  prepStepBreakdown?: string;
  prepMachineCodes?: string[];
  prepTotalMinutes?: number | null;
  assignmentKey?: string;
  /** BUG-2026-0194 — every weekly assignmentKey folded into a merged task bar. */
  assignmentKeys?: string[];
  /** BUG-2026-0194 — how many weekly rows the bar folds (tooltip). */
  weekCount?: number;
  /** BUG-2026-0194 — allocation share of the job (tooltip); null = unknown. */
  sharePercent?: number | null;
  userKey?: string;
  weekStart?: string;         // weekStart of the assignment (Saturday ISO)
  projectKey?: string;
  workOrderKey?: string;
  conflictType?: 'hard' | 'soft' | null;
  scheduleTaskKey?: string | null;
  machineKey?: string;
  status?: string;
  source?: string;
  /** Item 6.2 — true for a synthetic IndexAllocation bar. */
  isIndexAllocation?: boolean;
  /** Item 6.2 — the allocation's ScenarioKey, used to deep-link to Phase 3. */
  scenarioKey?: string | null;
}

/** Minimal Highcharts GanttPoint shape we need to resolve a deep-link. */
export interface DeepLinkPoint {
  taskType: GanttPointTaskType | string; // string fallback for unknown types
  custom?: GanttPointCustom;
}

/** Navigation descriptor returned by resolveDeepLinkRoute. */
export interface RouteDescriptor {
  /** React Router path string (no query string included). */
  path: string;
  /** Query params to append.  Empty object = no query string. */
  search: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/** Build a URLSearchParams-compatible query string from a record. */
export function buildSearch(params: Record<string, string>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
}

// ---------------------------------------------------------------------------
// Main export — pure, unit-testable
// ---------------------------------------------------------------------------

/**
 * Resolve a Highcharts Gantt point to a navigation target.
 *
 * Called AFTER the caller has already checked for conflict navigation
 * (item 4) — i.e. this function only handles non-conflict bars.
 *
 * @param point  A plain object with at minimum `taskType` and optionally `custom`.
 * @returns RouteDescriptor or null for non-navigable points.
 */
export function resolveDeepLinkRoute(point: DeepLinkPoint): RouteDescriptor | null {
  const custom = point.custom ?? {};

  switch (point.taskType as GanttPointTaskType) {

    case 'machineBooking': {
      // Item 6.2 — Index-allocation bars deep-link to the Scenario Planner's
      // Index tests tab instead of the Machine Schedule, since the
      // allocation isn't a real MachineBooking row to focus there.
      // Renumbered from phase=3 to phase=2 by Item 9.1 (R1 — owner revision
      // 2026-07-23: Machine → Index → Employee planning).
      if (custom.isIndexAllocation) {
        const scenarioKey = custom.scenarioKey;
        if (!scenarioKey) return null;
        return {
          path: ROUTES.SCHEDULE_SCENARIO,
          search: { scenarioKey, phase: '2' },
        };
      }
      const bookingKey = custom.bookingKey;
      if (!bookingKey) return null;
      return {
        path: ROUTES.SCHEDULE_MACHINE,
        search: { focusBookingKey: bookingKey },
      };
    }

    case 'employeeAssignment': {
      const userKey = custom.userKey;
      const weekStart = custom.weekStart;
      if (!userKey || !weekStart) return null;
      return {
        path: ROUTES.SCHEDULE_EMPLOYEE,
        search: {
          focusUserKey: userKey,
          focusWeekStart: weekStart,
        },
      };
    }

    case 'workOrder': {
      // We navigate to the flat Work Orders tab because the WO point's
      // custom bag only carries workOrderKey — not the parent projectId that
      // ROUTES.WORK_ORDER_DETAIL requires.  A future enhancement can resolve
      // the project from the master Gantt response and build the full path.
      return {
        path: ROUTES.WORK_ORDERS_TAB,
        search: {},
      };
    }

    case 'project': {
      // Navigate to the Projects list.  A per-project detail route does not
      // exist yet (/projects/:projectId is only a WO listing pattern).
      return {
        path: ROUTES.PROJECTS,
        search: {},
      };
    }

    // Group rows are not navigable — they are just structural nodes.
    case 'machinesGroup':
    case 'staffGroup':
    case 'machineSummary':
    case 'personSummary':
      return null;

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Bar-context lookup — used to populate the read-only details drawer
// ---------------------------------------------------------------------------

/**
 * The full parent + row context for a clicked leaf bar, looked up from the
 * current (filtered) Master-Gantt response. One of `booking` / `assignment` is
 * set, depending on the bar's taskType.
 */
export interface GanttBarContext {
  kind: 'machineBooking' | 'employeeAssignment';
  projectId: string;
  projectName: string;
  workOrderNumber: string;
  workOrderName: string | null;
  booking?: MasterGanttMachineBooking;
  /**
   * BUG-2026-0193 — every prep booking folded into a merged "Preparation" bar
   * (including `booking`), so the drawer can span the bar and list its machines.
   */
  bookings?: MasterGanttMachineBooking[];
  /** Earliest weekly row of the bar (the one whose key is the bar id). */
  assignment?: MasterGanttEmployeeAssignment;
  /**
   * BUG-2026-0194 — every weekly row folded into a merged task bar (including
   * `assignment`), so the drawer can show the bar's full span.
   */
  assignments?: MasterGanttEmployeeAssignment[];
}

/**
 * Find the full context (parent project + work order + the matched row) for a
 * leaf Gantt point, by scanning the response for the row whose key matches the
 * point's custom bag. Returns null for group rows or when no match is found.
 *
 * Pure / side-effect-free — takes a plain point + response and returns data.
 */
export function findBarContext(
  point: DeepLinkPoint,
  response: MasterGanttResponse | null,
): GanttBarContext | null {
  if (!response) return null;
  const custom = point.custom ?? {};

  if (point.taskType === 'machineBooking') {
    const bookingKey = custom.bookingKey;
    if (!bookingKey) return null;
    // BUG-2026-0193: a folded prep bar lists every member key; gather them all
    // (they live under the same work order) so the drawer spans the bar.
    const wanted = new Set<string>(custom.bookingKeys?.length ? custom.bookingKeys : [bookingKey]);
    for (const project of response.projects) {
      for (const wo of project.workOrders) {
        const booking = wo.machineBookings.find((b) => b.bookingKey === bookingKey);
        if (booking) {
          const bookings = wo.machineBookings.filter((b) => wanted.has(b.bookingKey));
          return {
            kind: 'machineBooking',
            projectId: project.projectId,
            projectName: project.projectName,
            workOrderNumber: wo.workOrderNumber,
            workOrderName: wo.workOrderName,
            booking,
            bookings: bookings.length ? bookings : [booking],
          };
        }
      }
    }
    return null;
  }

  if (point.taskType === 'employeeAssignment') {
    const assignmentKey = custom.assignmentKey;
    if (!assignmentKey) return null;
    // BUG-2026-0194: a merged bar lists every folded weekly key; gather them
    // all (they live under the same work order) so the drawer spans the bar.
    const wanted = new Set<string>(custom.assignmentKeys?.length ? custom.assignmentKeys : [assignmentKey]);
    for (const project of response.projects) {
      for (const wo of project.workOrders) {
        const assignment = wo.employeeAssignments.find(
          (a) => a.assignmentKey === assignmentKey,
        );
        if (assignment) {
          const assignments = wo.employeeAssignments.filter((a) => wanted.has(a.assignmentKey));
          return {
            kind: 'employeeAssignment',
            projectId: project.projectId,
            projectName: project.projectName,
            workOrderNumber: wo.workOrderNumber,
            workOrderName: wo.workOrderName,
            assignment,
            assignments: assignments.length ? assignments : [assignment],
          };
        }
      }
    }
    return null;
  }

  return null;
}

/**
 * Build the read-only drawer detail for a leaf bar from its resolved context.
 *
 * Pure / presentation-only: composes display labels and inclusive display
 * dates (Unix ms) from the matched booking/assignment row. `barName` is the
 * Highcharts point.name (the human label already shown on the bar); used as the
 * drawer title with a sensible fallback.
 */
export function buildBarDetail(
  ctx: GanttBarContext,
  barName: string | null | undefined,
  conflictType: 'hard' | 'soft' | null,
  canOpenInSchedule: boolean,
): GanttBarDetail {
  const projectLabel = `${ctx.projectId}: ${ctx.projectName}`;
  const workOrderLabel = ctx.workOrderName
    ? `${ctx.workOrderNumber}: ${ctx.workOrderName}`
    : ctx.workOrderNumber;

  if (ctx.kind === 'machineBooking' && ctx.booking) {
    const b = ctx.booking;
    // BUG-2026-0193: a folded "Preparation" bar spans every member booking,
    // lists each machine once, and reads Committed only when all members are.
    const members = ctx.bookings?.length ? ctx.bookings : [b];
    const merged = members.length > 1;
    let start = b.startDate;
    let end = b.endDate;
    const machines: string[] = [];
    let allCommitted = true;
    for (const m of members) {
      if (m.startDate < start) start = m.startDate;
      if (m.endDate > end) end = m.endDate;
      const label = m.machineName ? `${m.machineCode} — ${m.machineName}` : m.machineCode;
      if (!machines.includes(label)) machines.push(label);
      if (m.status !== 'Committed') allCommitted = false;
    }
    return {
      kind: 'machineBooking',
      title: String(barName ?? b.taskName ?? b.machineCode),
      status: merged ? (allCommitted ? 'Committed' : 'Tentative') : (b.status ?? null),
      source: b.source ?? null,
      eventType: b.eventType ?? null,
      machine: machines.join(', '),
      task: merged
        ? `Preparation — ${members.length} prep steps shown as one bar (${prepStepBreakdown(members.map((m) => m.taskName))})`
        : (b.taskName ?? null),
      project: projectLabel,
      workOrder: workOrderLabel,
      labSection: b.machineLabSection ?? null,
      start: toMs(start) ?? null,
      end: toMs(end) ?? null,
      conflictType,
      canOpenInSchedule,
      // Item 6.2 — Index-allocation bars open the Scenario Planner, not the
      // Machine Schedule (see resolveDeepLinkRoute's isIndexAllocation branch).
      openInScheduleLabel: b.isIndexAllocation
        ? 'Open in Scenario Planner'
        : 'Open in Machine Schedule',
    };
  }

  const a = ctx.assignment!;
  // BUG-2026-0194: span every folded weekly row (inclusive last day for
  // display: explicit endDate, else weekStart+6). A single-week bar reduces to
  // its own dates. Status reads Committed only when every week is Committed.
  const rows = ctx.assignments?.length ? ctx.assignments : [a];
  let start: number | null = null;
  let end: number | null = null;
  let allCommitted = true;
  for (const r of rows) {
    const s = toMs(r.startDate ?? r.weekStart) ?? null;
    const e = toMs(r.endDate ?? addDays(r.weekStart, 6)) ?? null;
    if (s !== null && (start === null || s < start)) start = s;
    if (e !== null && (end === null || e > end)) end = e;
    if (r.status !== 'Committed') allCommitted = false;
  }
  return {
    kind: 'employeeAssignment',
    title: String(barName ?? a.displayName),
    status: rows.length > 1 ? (allCommitted ? 'Committed' : 'Tentative') : (a.status ?? null),
    source: a.source ?? null,
    person: a.displayName ?? null,
    task: a.taskName ?? null,
    project: projectLabel,
    workOrder: workOrderLabel,
    start,
    end,
    conflictType,
    canOpenInSchedule,
    openInScheduleLabel: 'Open in Employee Schedule',
  };
}
